import { useEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  CornerDownLeft,
  Navigation,
  Pause,
  Power,
  Square,
  Undo2,
} from 'lucide-react';
import type { Command, CommandPriority, SpeedType } from '@fabricos/protocol-ts';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { cancelGoto } from '@/lib/cancelGoto';
import { sendGoto, sendGotoFirmware } from '@/lib/gotoSend';
import { useFleet } from '@/store/fleet';
import { useGotoTargets, distMeters } from '@/store/gotoTargets';
import { sendCommand } from '@/hooks/useHubSocket';
import { log } from '@/lib/logger';

interface CommandPanelProps {
  robotId: string;
}

function makeId(): string {
  return `cmd-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
}

function arm(robotId: string): Command {
  return { kind: 'arm', id: makeId(), robot_id: robotId, priority: 'normal' as CommandPriority };
}
function disarm(robotId: string): Command {
  return { kind: 'disarm', id: makeId(), robot_id: robotId, priority: 'normal' };
}
function takeoff(robotId: string, altM = 10): Command {
  return { kind: 'takeoff', id: makeId(), robot_id: robotId, priority: 'normal', alt_m: altM };
}
function land(robotId: string): Command {
  return { kind: 'land', id: makeId(), robot_id: robotId, priority: 'normal' };
}
function rtl(robotId: string): Command {
  return { kind: 'rtl', id: makeId(), robot_id: robotId, priority: 'normal' };
}
function hold(robotId: string): Command {
  // BRAKE, not LOITER: with no RC attached, LOITER reads throttle-low and
  // descends — BRAKE stops and holds regardless (see lib/cancelGoto).
  return {
    kind: 'set_mode',
    id: makeId(),
    robot_id: robotId,
    priority: 'normal',
    mode: 'brake',
  };
}
function eStop(robotId: string): Command {
  return {
    kind: 'emergency_stop',
    id: makeId(),
    robot_id: robotId,
    priority: 'emergency',
  };
}
const RETRACE_LEG_M = 10; // min spacing between retrace waypoints
const RETRACE_MAX_LEGS = 20;

/** Speed-row UI state survives panel remounts (operator: the selector kept
 *  snapping back to GND SPD after SET). Module-level, per robot. */
const speedUiCache = new Map<string, { type: SpeedType; ms: number }>();

/**
 * Fly the trail backwards: sample the breadcrumb (newest → oldest) into
 * legs ≥ RETRACE_LEG_M apart and feed them through the GOTO queue — the
 * first leg replaces the active target, the rest promote on arrival.
 */
function retracePath(robotId: string): number {
  const trail = useFleet.getState().trails[robotId] ?? [];
  if (trail.length < 2) return 0;
  const legs: { lat: number; lon: number }[] = [];
  let anchor = trail[trail.length - 1]!; // current position — skip as a leg
  for (let i = trail.length - 2; i >= 0 && legs.length < RETRACE_MAX_LEGS; i--) {
    const p = trail[i]!;
    if (distMeters(anchor, p) >= RETRACE_LEG_M) {
      legs.push({ lat: p.lat, lon: p.lon });
      anchor = p;
    }
  }
  legs.forEach((p, i) =>
    sendGoto(robotId, p.lat, p.lon, { append: i > 0, skipConfirm: true }),
  );
  return legs.length;
}

function setYaw(robotId: string, yawDeg: number): Command {
  return {
    kind: 'set_yaw',
    id: makeId(),
    robot_id: robotId,
    priority: 'normal',
    yaw_deg: ((yawDeg % 360) + 360) % 360,
  };
}

function changeSpeed(robotId: string, speedType: SpeedType, speedMs: number): Command {
  return {
    kind: 'change_speed',
    id: makeId(),
    robot_id: robotId,
    priority: 'normal',
    speed_type: speedType,
    speed_ms: speedMs,
  };
}

export function CommandPanel({ robotId }: CommandPanelProps) {
  const state = useFleet((s) => s.states[robotId]);
  const gotoTarget = useGotoTargets((s) => s.targets[robotId]);
  const gotoAlt = useGotoTargets((s) => s.settings.defaultAltAglM);
  const setGotoSettings = useGotoTargets((s) => s.setSettings);
  const armed = state?.arm === 'armed';
  const inFlight = (state?.position?.alt_rel_home_m ?? 0) > 0.5;

  const [takeoffAlt, setTakeoffAlt] = useState(10);
  const cached = speedUiCache.get(robotId);
  const [speedType, setSpeedTypeRaw] = useState<SpeedType>(cached?.type ?? 'ground');
  const [speedMs, setSpeedMsRaw] = useState(cached?.ms ?? 5);
  function setSpeedType(t: SpeedType) {
    setSpeedTypeRaw(t);
    speedUiCache.set(robotId, { type: t, ms: speedMs });
  }
  function setSpeedMs(v: number) {
    setSpeedMsRaw(v);
    speedUiCache.set(robotId, { type: speedType, ms: v });
  }
  const [gotoInput, setGotoInput] = useState('');
  // Edited-but-not-applied alt → GO goes green to signal "click me".
  const [altDirty, setAltDirty] = useState(false);
  /** Arrow-key streak tracker for accelerated alt stepping (1 m → 5 m). */
  const altStepRef = useRef({ last: 0, streak: 0 });
  const gotoInputValid = /^(-?\d+(\.\d+)?)[,\s]+(-?\d+(\.\d+)?)$/.test(gotoInput.trim());

  function sendManualGoto() {
    const m = gotoInput.trim().match(/^(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)$/);
    if (!m) return;
    const lat = Number(m[1]);
    const lon = Number(m[2]);
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return;
    sendGoto(robotId, lat, lon);
    setGotoInput('');
  }

  /** GO next to the Alt field: apply the altitude NOW. With an active GOTO
   *  the drone continues to the same destination at the new height (queue
   *  untouched); idle, it climbs/descends in place. */
  function applyAltNow() {
    setAltDirty(false);
    const active = useGotoTargets.getState().setAlt(robotId, gotoAlt);
    if (active) {
      sendGotoFirmware(active);
      log.action('Altitude change — continue to target', { robotId, altM: gotoAlt });
      return;
    }
    const pos = useFleet.getState().states[robotId]?.position;
    if (!pos) return;
    sendGotoFirmware({ robotId, lat: pos.lat, lon: pos.lon, alt_m: gotoAlt, ts_ms: Date.now() });
    log.action('Altitude change — in place', { robotId, altM: gotoAlt });
  }

  function fire(c: Command, label: string) {
    log.action(`${label} clicked`, { robotId, kind: c.kind });
    if (!sendCommand(c)) {
      console.warn('WS not open');
    }
  }

  return (
    <div className="space-y-2 rounded-lg border border-border-default bg-surface-2/40 p-3">
      <div className="grid grid-cols-2 gap-2">
        {!armed ? (
          <HoldButton
            label="ARM"
            durationMs={1000}
            onComplete={() => fire(arm(robotId), 'Arm')}
            tone="good"
            icon={<Power className="h-3.5 w-3.5" />}
          />
        ) : (
          <Button size="sm" variant="secondary" onClick={() => fire(disarm(robotId), 'Disarm')}>
            <Power className="h-3.5 w-3.5" />
            DISARM
          </Button>
        )}

        <Button
          size="sm"
          variant="primary"
          disabled={!armed || inFlight}
          onClick={() => fire(takeoff(robotId, takeoffAlt), 'Takeoff')}
        >
          <ArrowUp className="h-3.5 w-3.5" />
          Takeoff
        </Button>

        <Button
          size="sm"
          variant="secondary"
          disabled={!inFlight}
          onClick={() => fire(land(robotId), 'Land')}
        >
          <ArrowDown className="h-3.5 w-3.5" />
          Land
        </Button>

        <Button
          size="sm"
          variant="secondary"
          disabled={!inFlight}
          onClick={() => fire(rtl(robotId), 'RTL')}
        >
          <CornerDownLeft className="h-3.5 w-3.5" />
          RTL
        </Button>

        <Button
          size="sm"
          variant="secondary"
          disabled={!inFlight}
          title="LOITER in place — also cancels any active GOTO and its queue"
          onClick={() => {
            // Hold absorbs "Cancel GOTO" (operator call 2026-07-14: both
            // LOITERed — one button). cancelGoto = LOITER + clear targets.
            if (gotoTarget) {
              log.action('Hold clicked (cancels GOTO)', { robotId });
              cancelGoto(robotId);
            } else {
              fire(hold(robotId), 'Hold');
            }
          }}
        >
          <Pause className="h-3.5 w-3.5" />
          Hold
        </Button>

        <HoldButton
          label="E-STOP"
          durationMs={2000}
          onComplete={() => fire(eStop(robotId), 'E-Stop')}
          tone="critical"
          icon={<Square className="h-3.5 w-3.5" />}
        />
      </div>

      {/* One altitude control: takeoff alt on the ground, GOTO target alt
          once airborne (edits the value a Direct-mode map click uses). */}
      {!inFlight ? (
        <div className="flex items-center gap-2 text-[10px] text-fg-muted">
          <label htmlFor="alt-input">Takeoff alt</label>
          <input
            id="alt-input"
            type="number"
            step="1"
            min={2}
            max={120}
            value={takeoffAlt}
            onChange={(e) => setTakeoffAlt(Number(e.target.value))}
            className="h-6 w-16 rounded border border-border-default bg-surface-1 px-1.5 text-xs"
          />
          <span>m</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[10px] text-fg-muted">
          <label
            htmlFor="goto-alt-input"
            title="Altitude AGL for future GOTOs. GO applies it immediately — climb/descend now."
          >
            Alt
          </label>
          <input
            id="goto-alt-input"
            type="number"
            step="1"
            min={2}
            max={400}
            value={gotoAlt}
            onChange={(e) => {
              setGotoSettings({
                defaultAltAglM: Math.max(2, Math.min(400, Math.round(Number(e.target.value) || 2))),
              });
              setAltDirty(true);
            }}
            onKeyDown={(e) => {
              // Arrow stepping accelerates: ±1 m per press, jumping to ±5 m
              // after five rapid steps (holding the key auto-repeats).
              if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
              e.preventDefault();
              const now = Date.now();
              const streak = now - altStepRef.current.last < 350 ? altStepRef.current.streak + 1 : 0;
              altStepRef.current = { last: now, streak };
              const mag = streak >= 5 ? 5 : 1;
              const delta = e.key === 'ArrowUp' ? mag : -mag;
              setGotoSettings({
                defaultAltAglM: Math.max(2, Math.min(400, Math.round(gotoAlt + delta))),
              });
              setAltDirty(true);
            }}
            className="h-6 w-16 rounded border border-border-default bg-surface-1 px-1.5 text-xs"
          />
          <span>m AGL</span>
          <Button
            size="sm"
            variant="secondary"
            className={cn(
              '!h-6 !px-2 ml-auto',
              altDirty &&
                '!border-status-good !bg-status-good/15 !text-status-good hover:!bg-status-good/25',
            )}
            onClick={applyAltNow}
            title="Climb/descend to this altitude now — keeps heading to the active GOTO if there is one"
          >
            GO
          </Button>
        </div>
      )}

      {/* Manual GOTO — paste/type "lat, lon"; flies at the GOTO alt above.
          Same path as a map click (confirm rules, marker, history). */}
      {inFlight && (
        <div className="flex items-center gap-2 text-[10px] text-fg-muted">
          <label htmlFor="goto-coords">GOTO</label>
          <input
            id="goto-coords"
            value={gotoInput}
            onChange={(e) => setGotoInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') sendManualGoto();
            }}
            placeholder="lat, lng"
            className="h-6 min-w-0 flex-1 rounded border border-border-default bg-surface-1 px-1.5 font-mono text-[11px] text-fg-primary placeholder:text-fg-disabled"
          />
          <Button
            size="sm"
            variant="secondary"
            className={cn(
              '!h-6 !px-2',
              gotoInputValid &&
                '!border-status-good !bg-status-good/15 !text-status-good hover:!bg-status-good/25',
            )}
            disabled={!gotoInputValid}
            onClick={sendManualGoto}
            title="Fly to these coordinates at the GOTO altitude"
          >
            GO
          </Button>
        </div>
      )}

      {/* Retrace — only needs a flown trail. (Cancel GOTO strip removed:
          Hold above covers it — same LOITER, plus target/queue clear.) */}
      {inFlight && (
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => {
              const n = retracePath(robotId);
              if (n === 0) console.warn('retrace: trail too short');
            }}
            title="Fly back along the path just flown (breadcrumb legs, queued)"
            className="flex h-6 flex-1 items-center justify-center gap-1 rounded border border-border-default bg-surface-1 font-mono text-[10px] uppercase tracking-wide text-fg-secondary hover:border-accent/60 hover:text-accent"
          >
            <Undo2 className="h-3 w-3" />
            Retrace
          </button>
        </div>
      )}

      {/* Heading — horizontal compass tape + degrees input. SET sends
          MAV_CMD_CONDITION_YAW (absolute, shortest turn, default rate). */}
      {inFlight && (
        <HeadingRow
          currentDeg={
            state?.attitude != null
              ? ((state.attitude.yaw_rad * 180) / Math.PI + 360) % 360
              : null
          }
          courseDeg={(() => {
            // Course over ground from NED velocity — only meaningful while
            // actually moving.
            const v = state?.velocity;
            if (!v || (v.ground_speed_ms ?? 0) < 0.5) return null;
            return ((Math.atan2(v.vy_ms, v.vx_ms) * 180) / Math.PI + 360) % 360;
          })()}
          onSet={(deg) => fire(setYaw(robotId, deg), 'Set heading')}
        />
      )}

      {/* Live speed change — MAV_CMD_DO_CHANGE_SPEED, applies immediately in
          GUIDED/AUTO and lasts until the next mode change. */}
      {inFlight && (
        <div className="flex items-center gap-2 text-[10px] text-fg-muted">
          <select
            aria-label="Which speed to change"
            value={speedType}
            onChange={(e) => setSpeedType(e.target.value as SpeedType)}
            className="h-6 rounded border border-border-default bg-surface-1 px-1 text-[10px] uppercase text-fg-primary"
          >
            <option value="ground">GND SPD</option>
            <option value="climb">CLIMB</option>
            <option value="descent">DESCENT</option>
          </select>
          <input
            aria-label="Speed in metres per second"
            type="number"
            step="0.5"
            min={0.5}
            max={20}
            value={speedMs}
            onChange={(e) =>
              setSpeedMs(Math.max(0.5, Math.min(20, Number(e.target.value) || 0.5)))
            }
            className="h-6 w-16 rounded border border-border-default bg-surface-1 px-1.5 text-xs"
          />
          <span>m/s</span>
          <Button
            size="sm"
            variant="secondary"
            className="!h-6 !px-2 ml-auto"
            onClick={() => fire(changeSpeed(robotId, speedType, speedMs), 'Change speed')}
            title="Send DO_CHANGE_SPEED — applies now, lasts until the next mode change"
          >
            SET
          </Button>
        </div>
      )}
    </div>
  );
}

const CARDINALS: [number, string][] = [
  [0, 'N'],
  [45, 'NE'],
  [90, 'E'],
  [135, 'SE'],
  [180, 'S'],
  [225, 'SW'],
  [270, 'W'],
  [315, 'NW'],
];

/**
 * Horizontal compass controller: a 0–360° tape you click/drag to pick a
 * heading, synced with a degrees input. Cyan tick = drone's live heading;
 * amber tick = the selected target. SET goes green while unapplied.
 */
function HeadingRow({
  currentDeg,
  courseDeg,
  onSet,
}: {
  currentDeg: number | null;
  /** Course over ground while moving; null when hovering/stationary. */
  courseDeg: number | null;
  onSet: (deg: number) => void;
}) {
  const [deg, setDegRaw] = useState(0);
  const [dirty, setDirty] = useState(false);
  const tapeRef = useRef<HTMLDivElement | null>(null);

  function setDeg(v: number) {
    setDegRaw(((Math.round(v) % 360) + 360) % 360);
    setDirty(true);
  }

  function degFromPointer(e: React.PointerEvent) {
    const el = tapeRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    setDeg(frac * 359.99);
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-[10px] text-fg-muted">
        <label htmlFor="heading-input">HDG</label>
        <input
          id="heading-input"
          type="number"
          min={0}
          max={359}
          step={1}
          value={deg}
          onChange={(e) => setDeg(Number(e.target.value) || 0)}
          className="h-6 w-16 rounded border border-border-default bg-surface-1 px-1.5 text-xs"
        />
        <span>°</span>
        {courseDeg != null && (
          <Button
            size="sm"
            variant="secondary"
            className="!h-6 !px-2 ml-auto"
            onClick={() => {
              const c = Math.round(courseDeg) % 360;
              setDegRaw(c);
              setDirty(false);
              onSet(c);
            }}
            title={`Face the direction of travel (${Math.round(courseDeg)}°) — sends immediately`}
          >
            <Navigation className="h-3 w-3" />
            TRACK
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          className={cn(
            '!h-6 !px-2',
            courseDeg == null && 'ml-auto',
            dirty &&
              '!border-status-good !bg-status-good/15 !text-status-good hover:!bg-status-good/25',
          )}
          onClick={() => {
            setDirty(false);
            onSet(deg);
          }}
          title="Rotate to this heading now — CONDITION_YAW, shortest turn"
        >
          SET
        </Button>
      </div>
      <div
        ref={tapeRef}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
          degFromPointer(e);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) degFromPointer(e);
        }}
        title="Click or drag to pick a heading"
        className="relative h-7 cursor-ew-resize touch-none select-none overflow-hidden rounded border border-border-default bg-surface-1"
      >
        {/* Minor ticks every 15° */}
        {Array.from({ length: 24 }, (_, i) => i * 15).map((d) => (
          <span
            key={d}
            className="absolute bottom-0 w-px bg-border-default"
            style={{ left: `${(d / 360) * 100}%`, height: d % 45 === 0 ? '10px' : '5px' }}
          />
        ))}
        {/* Cardinal labels */}
        {CARDINALS.map(([d, label]) => (
          <span
            key={label}
            className="absolute top-0.5 -translate-x-1/2 font-mono text-[8.5px] text-fg-muted"
            style={{ left: `${(d / 360) * 100}%` }}
          >
            {label}
          </span>
        ))}
        {/* Live drone heading (cyan) */}
        {currentDeg != null && (
          <span
            className="pointer-events-none absolute inset-y-0 w-[2px] bg-accent/70"
            style={{ left: `${(currentDeg / 360) * 100}%` }}
            title="Current heading"
          />
        )}
        {/* Selected target (amber) */}
        <span
          className="pointer-events-none absolute inset-y-0 w-[2px] bg-amber-400"
          style={{ left: `${(deg / 360) * 100}%` }}
        />
      </div>
    </div>
  );
}

interface HoldButtonProps {
  label: string;
  /** Time for the progress bar to fill, in ms. */
  durationMs: number;
  onComplete: () => void;
  tone: 'good' | 'critical';
  icon?: React.ReactNode;
}

/**
 * Click-to-arm progress button. First click starts a countdown that fills the
 * button left-to-right; if the bar completes, `onComplete` fires. Clicking
 * again during the countdown cancels — gives the operator a deliberate but
 * reversible commit instead of a fragile mouse-hold.
 *
 * Name kept as `HoldButton` for call-site stability; behaviour is now
 * click-to-progress with cancel.
 */
function HoldButton({ label, durationMs, onComplete, tone, icon }: HoldButtonProps) {
  const [active, setActive] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  function clearPending() {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }

  function onClick() {
    if (active) {
      // Mid-countdown cancel.
      clearPending();
      setActive(false);
      return;
    }
    setActive(true);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      // Fire the action FIRST, then reset state. Order matters so a render
      // race can't drop the call.
      try {
        onCompleteRef.current();
      } finally {
        setActive(false);
      }
    }, durationMs);
  }

  // Clean up if the button unmounts mid-countdown.
  useEffect(() => () => clearPending(), []);

  const fill = tone === 'good' ? 'bg-status-good/70' : 'bg-status-critical/70';
  const ring =
    tone === 'good' ? 'border-status-good' : 'border-status-critical';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex h-9 select-none items-center justify-center gap-1.5 overflow-hidden rounded border text-xs font-semibold uppercase tracking-wide',
        ring,
        'text-fg-primary hover:bg-surface-3',
        active && 'bg-surface-3',
      )}
      title={active ? 'Click to cancel' : `Click to ${label.toLowerCase()}`}
    >
      <span
        className={cn('absolute inset-y-0 left-0', fill)}
        style={{
          width: active ? '100%' : '0%',
          // While active, transition linearly over the full duration so the
          // bar visually tracks the timer. On cancel/complete, snap back fast.
          transition: active
            ? `width ${durationMs}ms linear`
            : 'width 120ms ease-out',
        }}
      />
      <span className="relative z-10 flex items-center gap-1.5">
        {icon}
        {active ? `${label} · CANCEL` : label}
      </span>
    </button>
  );
}
